import Layout from '@src/components/Layout';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { Divider, Grid, Input } from 'semantic-ui-react';
import { StyledGridColumns, StyledGridRow, StyledInfoRow, StyledSegment } from '~/pages/Dairy/MilkFixedPrice/ShowMilkFixedPrice/style';
import { useAuth } from '~/routes/components/AuthProvider';
import { RouteTypes } from '~/routes/constants';
import { formatNumber, formatNumberWithoutFractions } from '~/utils/helpers';
import { useDebounce } from 'use-lodash-debounce';
import { FarmersQuery } from '~/QueryHooks/Farmers';
import { getFarmerTableColumnHeaders } from '~/Constants';
import TableComponent from '~/components/TableComponent';
import { StyledTableHeader } from './style';
import FarmerGroupQuery from '~/QueryHooks/FarmerGroupsQuery';

interface ShowFarmerGroupProps { }

const ShowFarmerGroup: React.FunctionComponent<ShowFarmerGroupProps> = () => {
  const { t } = useTranslation()
  const { id } = useParams()
  const { systemData: { amountUnit } } = useAuth()
  const [tableColumnHeaders, setTableColumnHeaders] = useState(getFarmerTableColumnHeaders(amountUnit))
  const [dataFiltered, setDataFiltered] = useState({
    page: 0,
    rows: 30,
    search: '',
    farmerGroupId: id
  })
  const [search, setSearch] = useState('')
  const debouncedSearchValue = useDebounce(search, 300)

  const {
    isLoading: isBusyData,
    data: farmersData,
    refetch: fetchFilteredData,
    isFetching: isFetchingData
  } = FarmersQuery.getFarmersList({
    ...dataFiltered,
    page: dataFiltered.page === 0 ? dataFiltered.page : dataFiltered.page - 1
  });

  const {
    isLoading: isBusyShow,
    data: showData,
    refetch: fetchShowData
  } = FarmerGroupQuery.getFarmerGroupsShowData(id);

  useEffect(() => {
    fetchShowData()
    setTableColumnHeaders(getFarmerTableColumnHeaders(amountUnit))
  }, [t])

  useEffect(() => {
    fetchFilteredData()
  }, [dataFiltered])

  useEffect(() => {
    setDataFiltered({ ...dataFiltered, search: debouncedSearchValue })
  }, [debouncedSearchValue])

  function renderSegment() {
    return <StyledSegment placeholder>
      <Grid columns={2} stackable>
        <Divider vertical />
        <Grid.Row verticalAlign='middle'>
          <Grid.Column>
            <Grid columns={5} stackable>
              <StyledGridRow>
                <Grid.Column>{t('ID / NAME')}</Grid.Column>
                <StyledGridColumns>{`${showData?.uniqueId || ''} / ${showData?.name || ''}`}</StyledGridColumns>
              </StyledGridRow>
              <StyledGridRow>
                <Grid.Column>{t('MGA Factor')}</Grid.Column>
                <StyledGridColumns>{showData.mgaFactorName || '/'}</StyledGridColumns>
              </StyledGridRow>
              <StyledInfoRow>
                <Grid.Column style={{ width: '19%', minWidth: '19%' }}>{t('Description')}</Grid.Column>
                <StyledGridColumns>{showData.description || '/'}</StyledGridColumns>
              </StyledInfoRow>
              <StyledInfoRow>
                <Grid.Column style={{ width: '19%', minWidth: '19%' }}>{t('Modules')}</Grid.Column>
                <StyledGridColumns>{showData.modules.map((module, index) => `${module.label}${index === showData?.modules.length - 1 ? '' : ','} `)}</StyledGridColumns>
              </StyledInfoRow>
            </Grid>
          </Grid.Column>
          <Grid.Column>
            <Grid columns={2} stackable>
              <StyledGridRow>
                <Grid.Column>{t('Total amount ({{amountUnit}} / year)', { amountUnit })}</Grid.Column>
                <StyledGridColumns>{formatNumberWithoutFractions(showData.farmersAmount || 0)} {amountUnit}</StyledGridColumns>
              </StyledGridRow>
              <StyledGridRow>
                <Grid.Column>{t('Trading amount (% / year)')}</Grid.Column>
                <StyledGridColumns>{`${formatNumber(showData.amountPercent)} %`}</StyledGridColumns>
              </StyledGridRow>
              <StyledGridRow>
                <Grid.Column>{t('Trading amount ({{amountUnit}} / year)', { amountUnit })}</Grid.Column>
                <StyledGridColumns>{formatNumberWithoutFractions(showData.farmersTradingAmount || 0)} {amountUnit}</StyledGridColumns>
              </StyledGridRow>
              <StyledGridRow>
                <Grid.Column>{t('Average trading amount ({{amountUnit}} / month)', { amountUnit })}</Grid.Column>
                <StyledGridColumns>{formatNumberWithoutFractions(showData?.farmersAverageTradingAmount || 0)} {amountUnit}</StyledGridColumns>
              </StyledGridRow>
              <StyledGridRow>
                <Grid.Column>{t('Minimum contingent (in {{amountUnit}})', { amountUnit })}</Grid.Column>
                <StyledGridColumns>{formatNumberWithoutFractions(showData.minimumContingent || 0)} {amountUnit}</StyledGridColumns>
              </StyledGridRow>
            </Grid>
          </Grid.Column>
        </Grid.Row>
      </Grid>
    </StyledSegment>
  }

  function renderTable() {
    return <div>
      <StyledTableHeader>
        <div className='title'>{t('Farmers')}</div>
        <Input
          icon={{ name: 'search', circular: true, link: true }}
          placeholder={t('Search...')}
          value={search}
          onChange={(e, data) => setSearch(data.value)}
        />
      </StyledTableHeader>
      <TableComponent
        isFetchingData={isFetchingData}
        animated
        handleChangeDataFiltered={(data, tableColumnHeaders) => {
          setTableColumnHeaders(tableColumnHeaders)
          setDataFiltered(data)
        }}
        tableColumnHeaders={tableColumnHeaders.filter(column => !!column.key)}
        dataFiltered={dataFiltered}
        rows={rows}
        total={filtered}
      />
    </div>
  }

  const { filtered, rows } = farmersData ? farmersData : { filtered: 0, rows: [] };

  return <Layout
    title={t('Show farmer group')}
    isBusy={isBusyShow || isBusyData}
    useSpinner
    childContainerSize={'300px'}
    fontSizeHeader={'18px'}
    close={RouteTypes.DAIRY_FACTORY_PROFILE_FARMER_GROUPS}
  >
    {showData && renderSegment()}
    {renderTable()}
  </Layout>;
};

export default ShowFarmerGroup;
